import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useLazyGetPrimaryServicesQuery,
  useLazyGetAddOnServicesQuery,
} from '../../services/orderApi';
import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '../../../../types/common';
import { isActiveService } from '../../../../util/service-catalog';
import type {
  ProductService,
  DisplayService,
  SelectedServiceData,
} from '../../../../types/order.types';
import {
  enrichService,
  recalculatePrices,
  computeTotals,
  slugToArr,
  arrToSlug,
} from './serviceHelpers';

export { slugToArr, arrToSlug } from './serviceHelpers';

/**
 * Canonical "previously saved selection" shape that the hook accepts.
 * Callers (wizard / cart) convert their native shape to this at the
 * boundary — that way the hook handles a single input type instead of
 * branching on cart-vs-wizard field names.
 */
export interface PreviousServiceSelection {
  shoppingCartSlug: string;
  quantity: number | null;
  price: number;
}

interface UseServiceSelectionProps {
  /** Selections to restore on mount (e.g. when reopening the cart slider). */
  previousSelections?: PreviousServiceSelection[] | null;
  propertyOccupancyStatus?: string;
  isCartOrder?: boolean;
  tokenData?: string;
}

export function useServiceSelection({
  previousSelections,
  propertyOccupancyStatus: externalOccupancy,
  isCartOrder,
}: UseServiceSelectionProps) {
  const dispatch = useDispatch();

  const [fetchPrimary, { isFetching: fetchingPrimary }] =
    useLazyGetPrimaryServicesQuery();
  const [fetchAddOns] = useLazyGetAddOnServicesQuery();

  const [services, setServices] = useState<DisplayService[]>([]);
  const servicesRef = useRef(services);
  servicesRef.current = services;
  const [occupancyStatus, setOccupancyStatus] = useState<string>(
    isCartOrder
      ? sessionStorage.getItem('propertyStatusCart') ?? 'vacant'
      : externalOccupancy ?? 'vacant',
  );
  const [isLoading, setIsLoading] = useState(true);
  // The selected primary's add-ons arrive after the selection itself. The
  // request id lets a newer selection, a deselect or a clear retire an older
  // fetch, so a late response never lands add-ons on the wrong package.
  const [isLoadingAddOns, setIsLoadingAddOns] = useState(false);
  const addOnRequestRef = useRef(0);
  const abandonAddOnFetch = useCallback(() => {
    addOnRequestRef.current += 1;
    setIsLoadingAddOns(false);
  }, []);

  const hasAnySelected = services.some((s) => s.isSelected);

  // ---- Occupancy filter ------------------------------------------------
  const visibleServices = useMemo(() => {
    if (occupancyStatus === 'occupied') {
      return services.filter((s) => s.availableOccupied);
    }
    return services;
  }, [services, occupancyStatus]);

  // ---- Totals -----------------------------------------------------------
  const totals = useMemo(() => computeTotals(services), [services]);

  // ---- Initial load -----------------------------------------------------
  useEffect(() => {
    loadServices();
    // Mount-only initial fetch; loadServices reads its closure for the
    // latest occupancy/services snapshots when called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAddOnsForSlug = async (
    slug: string,
  ): Promise<DisplayService[]> => {
    try {
      const raw = await fetchAddOns(slug).unwrap();
      // Add-on eligibility is the backend's prerequisite-keyed response; the
      // one client-side rule is `active` (DV-3586): a retired service must
      // not be offered even if the response still carries it. The guard
      // stays after products-api filters inactive add-ons itself (DV-3583).
      // `orderableOnline` is deliberately NOT applied — it gates only the
      // primary list, and some add-ons offered today (Enhanced Virtual
      // Staging's image select) are `orderableOnline: false`.
      return raw
        .filter(isActiveService)
        .map((s) => enrichService(s, 'secondary'))
        .sort((a, b) => (a.onlineSortOrder ?? 0) - (b.onlineSortOrder ?? 0));
    } catch {
      return [];
    }
  };

  const restorePreviousSelections = async (
    base: DisplayService[],
    previouslySelected: PreviousServiceSelection[],
  ): Promise<DisplayService[]> => {
    let result = [...base];

    for (const svc of result) {
      const match = previouslySelected.find(
        (ps) => ps.shoppingCartSlug === svc.shoppingCartSlug,
      );
      if (match) {
        svc.isSelected = true;
        if (match.quantity != null) svc.selectedQuantity = match.quantity;
        svc.selectedPrice = match.price;

        if (svc.type === 'primary') {
          const addOns = await loadAddOnsForSlug(svc.shoppingCartSlug);
          for (const ao of addOns) {
            const aoMatch = previouslySelected.find(
              (ps) => ps.shoppingCartSlug === ao.shoppingCartSlug,
            );
            if (aoMatch) {
              ao.isSelected = true;
              if (aoMatch.quantity != null) ao.selectedQuantity = aoMatch.quantity;
              ao.selectedPrice = aoMatch.price;
            }
          }
          result = [...result, ...addOns];
        }
      }
    }

    return recalculatePrices(result);
  };

  const loadServices = useCallback(async () => {
    setIsLoading(true);
    try {
      // preferCacheValue: the cart page has usually loaded this list already
      // (the DV-3204 floor plan offer reads it), and each read costs
      // products-api seconds. The list carries no cache tags, so nothing
      // invalidates it and a cached copy cannot race a refetch.
      const { data: rawServices } = await fetchPrimary(undefined, true)
        .unwrap()
        .then((d) => ({ data: d }))
        .catch(() => ({ data: [] as ProductService[] }));

      const active = rawServices.filter(
        (s) => isActiveService(s) && s.orderableOnline,
      );
      let enriched = active
        .map((s) => enrichService(s, 'primary'))
        .sort((a, b) => (a.onlineSortOrder ?? 0) - (b.onlineSortOrder ?? 0));

      if (previousSelections && previousSelections.length > 0) {
        enriched = await restorePreviousSelections(
          enriched,
          previousSelections,
        );
      }

      setServices(enriched);
    } finally {
      setIsLoading(false);
    }
    // loadServices is intentionally called only from the mount effect;
    // listing every closure value would either fire it on every prop change
    // or require splitting it into multiple stable callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Select -----------------------------------------------------------
  const selectService = useCallback(
    async (slug: string) => {
      setServices((prev) => {
        const slugArr = slugToArr(slug);
        const deselected = prev.map((s) => {
          const sArr = slugToArr(s.connectSlug);
          const overlaps = slugArr.some((sl) => sArr.includes(sl));
          return overlaps && s.isSelected ? { ...s, isSelected: false } : s;
        });

        const target = deselected.find(
          (s) => arrToSlug(slugToArr(s.connectSlug)) === slug,
        );
        if (!target) return deselected;

        // Selecting a primary clears the old add-ons and enforces the
        // single-primary invariant by deselecting any sibling primary.
        let cleaned =
          target.type === 'primary'
            ? deselected
                .filter((s) => s.type !== 'secondary')
                .map((s) =>
                  s.type === 'primary' && s.uuid !== target.uuid
                    ? { ...s, isSelected: false }
                    : s,
                )
            : deselected;

        cleaned = cleaned.map((s) =>
          s.uuid === target.uuid
            ? {
                ...s,
                isSelected: true,
                occupancyStatus:
                  localStorage.getItem('propertyOccupancyStatus') ??
                  occupancyStatus,
              }
            : s,
        );

        return recalculatePrices(cleaned);
      });

      // Use ref to avoid stale closure — setServices above already
      // updated state, but the `services` closure value is from the
      // previous render.
      const target = servicesRef.current.find(
        (s) => arrToSlug(slugToArr(s.connectSlug)) === slug,
      );
      if (target?.type === 'primary') {
        const requestId = ++addOnRequestRef.current;
        setIsLoadingAddOns(true);
        const addOns = await loadAddOnsForSlug(target.shoppingCartSlug);
        if (requestId !== addOnRequestRef.current) return;
        setIsLoadingAddOns(false);
        setServices((prev) => {
          const withoutOldAddOns = prev.filter((s) => s.type !== 'secondary');
          return recalculatePrices([...withoutOldAddOns, ...addOns]);
        });
      }
    },
    // Selection logic uses servicesRef + setServices(prev=>...) so the
    // services closure value isn't read directly. occupancyStatus is the
    // only signal that needs to retrigger a fresh callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [occupancyStatus],
  );

  // ---- Deselect ---------------------------------------------------------
  const deselectService = useCallback((slug: string) => {
    const deselected = servicesRef.current.find(
      (s) => arrToSlug(slugToArr(s.connectSlug)) === slug,
    );
    if (deselected?.type === 'primary') abandonAddOnFetch();
    setServices((prev) => {
      const target = prev.find(
        (s) => arrToSlug(slugToArr(s.connectSlug)) === slug,
      );
      if (!target) return prev;

      let updated = prev.map((s) =>
        s.uuid === target.uuid ? { ...s, isSelected: false } : s,
      );

      if (target.type === 'primary') {
        updated = updated.filter((s) => s.type !== 'secondary');
      }

      return recalculatePrices(updated);
    });
  }, [abandonAddOnFetch]);

  // ---- Quantity change --------------------------------------------------
  // Updates the quantity only — it must NOT flip isSelected. Selection has
  // side effects that live in selectService (add-on fetch, single-primary
  // invariant, old-add-on cleanup); an implicit select here would bypass
  // them, leaving a "selected" primary with no add-ons (DV-2465).
  // The price is not a parameter: recalculatePrices derives every
  // selectedPrice from the new quantity, so a caller-supplied price would be
  // overwritten on the same pass.
  const setServiceQuantity = useCallback((slug: string, quantity: number) => {
    setServices((prev) => {
      const updated = prev.map((s) =>
        arrToSlug(slugToArr(s.connectSlug)) === slug
          ? { ...s, selectedQuantity: quantity }
          : s,
      );
      return recalculatePrices(updated);
    });
  }, []);

  // ---- Occupancy --------------------------------------------------------
  const changeOccupancy = useCallback(
    (status: string) => {
      setOccupancyStatus(status);
      if (isCartOrder) {
        sessionStorage.setItem('propertyStatusCart', status);
      }
    },
    [isCartOrder],
  );

  // ---- Clear ------------------------------------------------------------
  const clearSelection = useCallback(() => {
    abandonAddOnFetch();
    setServices((prev) => {
      const cleared = prev
        .filter((s) => s.type !== 'secondary')
        .map((s) => ({ ...s, isSelected: false }));
      return recalculatePrices(cleared);
    });
  }, [abandonAddOnFetch]);

  // ---- Build SelectedServiceData for parent ----------------------------
  const buildSelectedServiceData =
    useCallback((): SelectedServiceData | null => {
      const selected = services.filter((s) => s.isSelected);
      if (selected.length === 0) {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message: 'Please select a service',
          }),
        );
        return null;
      }
      return {
        services: selected,
        occupancyStatus,
        totalPrice: totals.totalPrice,
        totalInitialPrice: totals.totalInitialPrice,
        totalSavingPrice: totals.totalSavingPrice,
      };
    }, [services, occupancyStatus, totals, dispatch]);

  return {
    services: visibleServices,
    allServices: services,
    isLoading: isLoading || fetchingPrimary,
    /** The selected primary's add-ons are still on their way. */
    isLoadingAddOns,
    occupancyStatus,
    hasAnySelected,
    totals,
    selectService,
    deselectService,
    setServiceQuantity,
    changeOccupancy,
    clearSelection,
    buildSelectedServiceData,
  };
}
